import { useQuery } from '@tanstack/react-query';
import { Flex, List, Typography } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import type { AxiosResponse } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { getRoleByPermission } from 'src/apis/master_data/role.api';
import { getApprovalProcessDetail } from 'src/apis/master_data/approvalProcess.api';
import Approved from 'src/assets/icons/approved.png';
import Created from 'src/assets/icons/created.png';
import Finished from 'src/assets/icons/finished.png';
import Rejected from 'src/assets/icons/rejected.png';
import Waiting from 'src/assets/icons/waiting.png';
import Icon from 'src/components/v2/Icon';
import { PermissionEn } from 'src/constants/permission.enum';
import urlPath from 'src/constants/urlPath';
import { DepartmentEn } from 'src/constants/department.enum';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import { TransportationTypeEn } from 'src/constants/transportationType.enum';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { useBasePath } from 'src/hooks/useBasePath';
import type { ILeavePackage } from 'src/types/hr_ticket/leavePackage.type';
import type { IRoleUser } from 'src/types/master_data/roleUser.type';
import { ApprovalStatus, TicketStatus } from 'src/types/helpdesk/ticket.type';
import type { IAdvancePaymentTicket } from 'src/types/hr_ticket/advancePaymentTicket.type';
import type { IBusinessTripTicket } from 'src/types/hr_ticket/businessTripTicket.type';
import type { IEquipmentSupplyRequestTicket } from 'src/types/hr_ticket/equipmentSupplyRequestTicket.type';
import type { ILeaveTicket } from 'src/types/hr_ticket/leaveTicket.type';
import type { IOvertimeTicket } from 'src/types/hr_ticket/overtimeTicket.type';
import type { IApprovalProcessDetail, IProcessTree, StatusType } from 'src/types/hr_ticket/processTree.type';
import type { IPurchaseRequestTicket } from 'src/types/hr_ticket/purchaseRequestTicket.type';
import type { ITransportationTicket } from 'src/types/hr_ticket/transportationTicket.type';
import { formatDate, formatTime } from 'src/utils/utils';
import type { IResignationTicket } from 'src/types/hr_ticket/resignationTicket.type';

import './TimelineApproval.scss';
import type { IAdditionalWorkingTimeTicket } from 'src/types/hr_ticket/additionalWorkingTimeTicket.type';
import type { IAdditionalWorkingTimePackage } from 'src/types/hr_ticket/additionalWorkingTimePackage.type';
import NoteText from './NoteText';

const { Text } = Typography;

interface IconType {
  status: StatusType;
  size?: number;
  className?: string;
}

const RenderIcon = ({ status, size = 16, className }: IconType) => {
  switch (status) {
    case 'approved':
      return <Icon className={className} url={Approved} size={size} titleTooltip='Đã duyệt' />;
    case 'waiting':
      return <Icon className={className} url={Waiting} size={size} titleTooltip='Đang đợi' />;
    case 'rejected':
      return <Icon className={className} url={Rejected} size={size} titleTooltip='Từ chối' />;
    case 'created':
      return <Icon className={className} url={Created} size={size} titleTooltip='Thêm mới thành công' />;
    case 'finished':
      return <Icon className={className} url={Finished} size={size} titleTooltip='Hoàn thành' />;
  }
};

type Props = {
  hrTicket:
    | AxiosResponse<
        | ILeaveTicket
        | ILeavePackage
        | IBusinessTripTicket
        | IOvertimeTicket
        | IAdvancePaymentTicket
        | ITransportationTicket
        | IPurchaseRequestTicket
        | IEquipmentSupplyRequestTicket
        | IAdditionalWorkingTimeTicket
        | IAdditionalWorkingTimePackage
        | IResignationTicket,
        any
      >
    | undefined;
  ticketType?: 'resignation' | '';
};

const TimelineApproval = ({ hrTicket, ticketType }: Props) => {
  const { loading } = useContext(HrTicketDetailContext);
  const [processTree, setProcessTree] = useState<IProcessTree[]>([]);
  const [employeesApprovalSelfSufficientTransportation, setEmployeesApprovalSelfSufficient] = useState<IRoleUser[]>();
  const [employeesApprovalOtherTransportation, setEmployeesApprovalOtherTransportation] = useState<IRoleUser[]>();
  const basePath = useBasePath();
  const isMatchConfirmTransportationPath =
    basePath?.includes(urlPath.transportationTicket.location) ||
    basePath?.includes(urlPath.hrConfirmTransportationTicket.location);
  const isMatchOvertimePath =
    basePath?.includes(urlPath.overtimeTicket.location) || basePath?.includes(urlPath.hrConfirmOvertimeTicket.location);
  const isMatchBusinessTripPath =
    basePath?.includes(urlPath.businessTripTicket.location) ||
    basePath?.includes(urlPath.hrConfirmBusinessTripTicket.location);

  // Get role confirm transportation
  const rolesByPermissionQuery = useQuery({
    queryKey: ['roleByPermissions'],
    queryFn: () => getRoleByPermission(PermissionEn.confirm_transportation),
    staleTime: 60 * 1000,
    enabled: isMatchConfirmTransportationPath
  });

  // Group by person confirm transportation
  useEffect(() => {
    if (rolesByPermissionQuery.data?.data) {
      let listEmployee: IRoleUser[] = [];
      rolesByPermissionQuery.data?.data.forEach((item) => listEmployee.push(...item.roleUsers));

      setEmployeesApprovalSelfSufficient(
        listEmployee.filter((item) => item.employee?.departmentId === DepartmentEn.hr)
      );
      setEmployeesApprovalOtherTransportation(
        listEmployee.filter((item) => item.employee?.departmentId !== DepartmentEn.hr)
      );
    }
  }, [rolesByPermissionQuery.data?.data]);

  // Get approval process
  const approvalProcessDetailQuery = useQuery({
    queryKey: [
      'approvalProcessDetail',
      hrTicket?.data?.approvalProcessId,
      hrTicket?.data?.approvalProcessVersion,
      hrTicket?.data?.createdBy
    ],
    queryFn: () =>
      getApprovalProcessDetail(
        hrTicket?.data?.approvalProcessId as number,
        hrTicket?.data?.approvalProcessVersion as number,
        hrTicket?.data?.createdById as number
      ),
    enabled:
      !!hrTicket?.data?.approvalProcessId && !!hrTicket?.data?.approvalProcessVersion && !!hrTicket?.data?.createdById
  });

  useEffect(() => {
    if (!!hrTicket?.data && approvalProcessDetailQuery.data?.data && approvalProcessDetailQuery.data.data.length > 0) {
      const newProcessTree: IProcessTree[] = [];

      // Tạo ticket
      const creatorObj: IProcessTree = {
        title: 'Tạo phiếu',
        type: 'create',
        createdBy: hrTicket.data.createdBy,
        createdDate: hrTicket.data.createdDate
      };
      newProcessTree.push(creatorObj);

      // Phê duyệt ticket
      let convertApprovalProcessDetail: IApprovalProcessDetail[] = [];

      approvalProcessDetailQuery.data.data?.forEach((item) => {
        const index = convertApprovalProcessDetail.findIndex(
          (itemConvert) => itemConvert.approvalLevel === item.approvalLevel
        );
        if (index === -1) {
          item.employee &&
            convertApprovalProcessDetail.push({
              approvalLevel: item.approvalLevel,
              stepName: item.stepName,
              employeeId: undefined,
              position: undefined,
              approval: undefined,
              employees: [item.employee]
            } as IApprovalProcessDetail);

          item.position &&
            convertApprovalProcessDetail.push({
              ...item,
              approvalLevel: item.approvalLevel,
              stepName: item.stepName,
              employeeId: undefined,
              position: item.position,
              approval: undefined,
              employees: []
            } as IApprovalProcessDetail);
        } else {
          convertApprovalProcessDetail[index].employees?.push(item.employee);
        }
      });
      convertApprovalProcessDetail = convertApprovalProcessDetail.sort(
        (itemX, itemY) => itemX.approvalLevel - itemY.approvalLevel
      );
      convertApprovalProcessDetail = convertApprovalProcessDetail.map((item) => {
        const findItemApproval = hrTicket?.data?.approvals?.find(
          (approval) => approval.step === item.approvalLevel && approval.stepName === item.stepName
        );

        if (findItemApproval) {
          return {
            ...item,
            approval: findItemApproval,
            statusType: findItemApproval.approvalStatus === 1 ? 'approved' : 'rejected'
          };
        } else {
          return {
            ...item
          };
        }
      });

      const approvalObj: IProcessTree = {
        title: 'Duyệt',
        type: 'approve',
        approvals: convertApprovalProcessDetail
      };
      newProcessTree.push(approvalObj);

      // Confirm Transportation
      const accepterConfirmObj: IProcessTree = {
        title: 'BP vận chuyển xác nhận',
        type: 'accepterConfirm'
      };
      newProcessTree.push(accepterConfirmObj);

      // Confirm Transportation
      const driverConfirmObj: IProcessTree = {
        title: 'Tài xế xác nhận',
        type: 'driverConfirm'
      };
      newProcessTree.push(driverConfirmObj);

      // Supervisor Confirm
      const confirmedSupervisorObj: IProcessTree = {
        title: 'Người giám sát xác nhận',
        type: 'confirmedSupervisor'
      };
      newProcessTree.push(confirmedSupervisorObj);

      // Raise Ticket Confirm
      const requesterConfirmObj: IProcessTree = {
        title: 'Người yêu cầu xác nhận',
        type: 'confirm'
      };
      newProcessTree.push(requesterConfirmObj);

      // Hr Confirm
      const hrConfirmObj: IProcessTree = {
        title: 'Nhân sự xác nhận',
        type: 'hrConfirm',
        isHrConfirmed: hrTicket.data.hrConfirmedType,
        createdDate: hrTicket.data.hrConfirmedDate
      };
      if (ticketType === 'resignation') {
        newProcessTree.splice(1, 0, hrConfirmObj);
      } else if (!isMatchConfirmTransportationPath) {
        newProcessTree.push(hrConfirmObj);
      }

      // Done
      const doneObj: IProcessTree = {
        title: 'Hoàn thành',
        type: 'done',
        createdDate: hrTicket.data.isHrConfirmed
          ? hrTicket.data.lastModifiedDate
          : hrTicket?.data?.approvals?.reverse()[0]?.createdDate
      };
      newProcessTree.push(doneObj);

      setProcessTree(newProcessTree);
    }
  }, [hrTicket?.data, approvalProcessDetailQuery.data?.data, hrTicket, ticketType, isMatchConfirmTransportationPath]);

  const RenderItem = (item: IProcessTree, basePath: string) => {
    if (item.type === 'create') {
      return (
        <List.Item className='timeline-approval-item' key={item.type}>
          <List.Item.Meta
            avatar={
              <Flex className='item-left' vertical align='center' justify='start'>
                <Text strong className='time'>
                  {formatTime(item.createdDate)}
                </Text>
                <Text strong className='date'>
                  {formatDate(item.createdDate)}
                </Text>
                <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
                  <RenderIcon status='created' className='icon-status' />
                </Flex>
              </Flex>
            }
            title={<Text strong>{item.title}</Text>}
            description={<Text>{item.createdBy?.fullName}</Text>}
          />
        </List.Item>
      );
    }

    if (item.type === 'approve') {
      return item.approvals
        ?.sort((x, y) => x.approvalLevel - y.approvalLevel)
        .map((itemV2, index) => {
          return (
            <List.Item className='timeline-approval-item' key={item?.approvals?.[index].approvalLevel}>
              <List.Item.Meta
                avatar={
                  <Flex className='item-left' vertical align='center' justify='start'>
                    <Text strong className='time'>
                      {formatTime(itemV2.approval?.createdDate)}
                    </Text>
                    <Text strong className='date'>
                      {formatDate(itemV2.approval?.createdDate)}
                    </Text>
                    <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
                      <RenderIcon status={itemV2.statusType ?? 'waiting'} className='icon-status' />
                    </Flex>
                  </Flex>
                }
                title={
                  <Flex justify='space-between' align='center' style={{ width: '100%' }}>
                    <Text strong>{itemV2.stepName}</Text>
                  </Flex>
                }
                description={
                  <Flex vertical gap={5}>
                    {!!itemV2.approval ? (
                      <Text>{itemV2.approval.approver?.fullName}</Text>
                    ) : itemV2.position ? (
                      <Text key={itemV2.position?.id} style={{ textTransform: 'uppercase' }}>
                        {itemV2.position?.name}
                      </Text>
                    ) : (
                      itemV2.employees?.map((employee) => <Text key={employee.id}>{employee.fullName}</Text>)
                    )}

                    {hrTicket?.data.approvals?.reverse()[0]?.note && (
                      <NoteText
                        note={hrTicket?.data.approvals?.reverse()[0]?.note}
                        label={itemV2.approval?.approvalStatus === ApprovalStatus.Reject ? 'Lý do' : 'Ghi chú'}
                      />
                    )}
                  </Flex>
                }
              />
            </List.Item>
          );
        });
    }

    // Người raise ticket xác nhận tăng ca, công tác
    if (item.type === 'confirm' && hrTicket?.data && (isMatchOvertimePath || isMatchBusinessTripPath)) {
      return (
        <List.Item className='timeline-approval-item' key={item.type}>
          <List.Item.Meta
            avatar={
              <Flex className='item-left' vertical align='center' justify='start'>
                {'confirmDate' in hrTicket?.data && hrTicket?.data.isConfirm !== ConfirmTypeEn.WaitingConfirm && (
                  <Flex vertical align='center'>
                    <Text strong className='time'>
                      {formatTime(hrTicket?.data.confirmDate)}
                    </Text>
                    <Text strong className='date'>
                      {formatDate(hrTicket?.data.confirmDate)}
                    </Text>
                  </Flex>
                )}
                <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
                  <RenderIcon
                    status={
                      hrTicket?.data.isConfirm === ConfirmTypeEn.Confirmed
                        ? 'approved'
                        : hrTicket?.data.isConfirm === ConfirmTypeEn.Reject
                        ? 'rejected'
                        : 'waiting'
                    }
                    className='icon-status'
                  />
                </Flex>
              </Flex>
            }
            title={<Text strong>{item.title}</Text>}
            description={
              <Flex vertical gap={5}>
                <Text style={{ textTransform: 'uppercase' }}>
                  {hrTicket && 'employeeRaiseTicket' in hrTicket.data
                    ? hrTicket?.data.employeeRaiseTicket?.fullName
                    : 'NGƯỜI YÊU CẦU'}
                </Text>
                {hrTicket?.data.confirmNote && (
                  <Paragraph
                    style={{ marginBlockEnd: 0 }}
                    ellipsis={{
                      rows: 2,
                      tooltip: {
                        placement: 'rightTop',
                        color: '#4f606b',
                        title: (
                          <Text>
                            <Text
                              style={{
                                color: '#d9d9d9'
                              }}
                            >
                              {hrTicket?.data.isConfirm === ConfirmTypeEn.Confirmed && 'Kết quả công việc: '}
                              {hrTicket?.data.isConfirm === ConfirmTypeEn.Reject && 'Lý do không xác nhận: '}
                            </Text>
                            <Text
                              style={{
                                color: 'white'
                              }}
                            >
                              {hrTicket?.data.confirmNote}
                            </Text>
                          </Text>
                        )
                      }
                    }}
                  >
                    <Text>
                      <Text type='secondary'>
                        {hrTicket?.data.isConfirm === ConfirmTypeEn.Confirmed && 'Kết quả công việc: '}
                        {hrTicket?.data.isConfirm === ConfirmTypeEn.Reject && 'Lý do không xác nhận: '}
                      </Text>
                      {hrTicket?.data.confirmNote}
                    </Text>
                  </Paragraph>
                )}
              </Flex>
            }
          />
        </List.Item>
      );
    }

    if (item.type === 'hrConfirm' && hrTicket?.data && 'hrConfirmedType' in hrTicket?.data) {
      return (
        <List.Item className='timeline-approval-item' key={item.type}>
          <List.Item.Meta
            avatar={
              <Flex className='item-left' vertical align='center' justify='start'>
                {hrTicket?.data.hrConfirmedType !== ConfirmTypeEn.WaitingConfirm && (
                  <Flex vertical align='center'>
                    <Text strong className='time'>
                      {formatTime(hrTicket?.data.hrConfirmedDate)}
                    </Text>
                    <Text strong className='date'>
                      {formatDate(hrTicket?.data.hrConfirmedDate)}
                    </Text>
                  </Flex>
                )}
                <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
                  <RenderIcon
                    status={
                      hrTicket?.data.hrConfirmedType === ConfirmTypeEn.Confirmed ||
                      hrTicket?.data.hrConfirmedType === ConfirmTypeEn.Synced
                        ? 'approved'
                        : hrTicket?.data.hrConfirmedType === ConfirmTypeEn.Reject
                        ? 'rejected'
                        : 'waiting'
                    }
                    className='icon-status'
                  />
                </Flex>
              </Flex>
            }
            title={<Text strong>{item.title}</Text>}
            description={
              <Flex vertical gap={5}>
                <Text style={{ textTransform: 'uppercase' }}>
                  {hrTicket?.data?.hrConfirmedBy?.fullName ?? 'Bộ phận nhân sự'}
                </Text>
                {item.isHrConfirmed === ConfirmTypeEn.Reject && hrTicket?.data.hrConfirmedNote && (
                  <Paragraph
                    style={{ marginBlockEnd: 0 }}
                    ellipsis={{
                      rows: 2,
                      tooltip: {
                        placement: 'rightTop',
                        color: '#4f606b',
                        title: (
                          <Text>
                            <Text style={{ color: '#d9d9d9' }}>Lý do: </Text>
                            <Text style={{ color: 'white' }}>{hrTicket?.data.hrConfirmedNote}</Text>
                          </Text>
                        )
                      }
                    }}
                  >
                    <Text>
                      <Text type='secondary'>Lý do: </Text>
                      {hrTicket?.data.hrConfirmedNote}
                    </Text>
                  </Paragraph>
                )}
              </Flex>
            }
          />
        </List.Item>
      );
    }

    if (item.type === 'accepterConfirm' && hrTicket?.data && isMatchConfirmTransportationPath) {
      if ('confirmedType' in hrTicket?.data) {
        return (
          <List.Item className='timeline-approval-item' key={item.type}>
            <List.Item.Meta
              avatar={
                <Flex className='item-left' vertical align='center' justify='start'>
                  {hrTicket?.data.confirmedType !== ConfirmTypeEn.WaitingConfirm && (
                    <Flex vertical align='center'>
                      <Text strong className='time'>
                        {formatTime(hrTicket?.data.confirmedDate)}
                      </Text>
                      <Text strong className='date'>
                        {formatDate(hrTicket?.data.confirmedDate)}
                      </Text>
                    </Flex>
                  )}
                  <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
                    <RenderIcon
                      status={
                        hrTicket?.data.confirmedType === ConfirmTypeEn.Confirmed
                          ? 'approved'
                          : hrTicket?.data.confirmedType === ConfirmTypeEn.Reject
                          ? 'rejected'
                          : 'waiting'
                      }
                      className='icon-status'
                    />
                  </Flex>
                </Flex>
              }
              title={<Text strong>Nhân sự xác nhận</Text>}
              description={
                <Flex vertical gap={8}>
                  {hrTicket.data.confirmedType === ConfirmTypeEn.Confirmed ? (
                    <Text>{hrTicket.data?.confirmedBy?.fullName}</Text>
                  ) : (
                    <>
                      {employeesApprovalSelfSufficientTransportation?.map((item) => (
                        <Text key={item.id}>{item.employee.fullName}</Text>
                      ))}
                    </>
                  )}
                  {hrTicket?.data.confirmedType !== ConfirmTypeEn.WaitingConfirm && (
                    <Flex vertical gap={4}>
                      {hrTicket.data.transportationTypeId === TransportationTypeEn.shuttle &&
                        (hrTicket.data.transportationTicketVerify ? (
                          <Flex vertical gap={4}>
                            <Paragraph
                              style={{ marginBlockEnd: 0 }}
                              ellipsis={{
                                rows: 2,
                                tooltip: {
                                  placement: 'rightTop',
                                  color: '#4f606b',
                                  title: (
                                    <Text>
                                      <Text style={{ color: '#d9d9d9' }}>Tài xế: </Text>
                                      <Text style={{ color: 'white' }}>
                                        {hrTicket.data.transportationTicketVerify?.driver?.fullName}
                                      </Text>
                                    </Text>
                                  )
                                }
                              }}
                            >
                              {hrTicket.data.transportationTicketVerify.driverId && (
                                <Text>
                                  <Text type='secondary'>Tài xế: </Text>
                                  <Text>{hrTicket.data.transportationTicketVerify?.driver?.fullName}</Text>
                                </Text>
                              )}
                            </Paragraph>
                            <Paragraph
                              style={{ marginBlockEnd: 0 }}
                              ellipsis={{
                                rows: 2,
                                tooltip: {
                                  placement: 'rightTop',
                                  color: '#4f606b',
                                  title: (
                                    <Text>
                                      <Text style={{ color: '#d9d9d9' }}>Phương tiện: </Text>
                                      <Text style={{ color: 'white' }}>
                                        {hrTicket.data.transportationTicketVerify.vehicle?.name}. Biển số{' '}
                                        {hrTicket.data.transportationTicketVerify.vehicle?.licensePlate}
                                      </Text>
                                    </Text>
                                  )
                                }
                              }}
                            >
                              {hrTicket.data.transportationTicketVerify.vehicleId && (
                                <Text>
                                  <Text type='secondary'>Phương tiện: </Text>
                                  <Text>
                                    {hrTicket.data.transportationTicketVerify.vehicle?.name}. Biển số{' '}
                                    {hrTicket.data.transportationTicketVerify.vehicle?.licensePlate}
                                  </Text>
                                </Text>
                              )}
                            </Paragraph>
                          </Flex>
                        ) : (
                          <Flex vertical gap={4}>
                            <Paragraph
                              style={{ marginBlockEnd: 0 }}
                              ellipsis={{
                                rows: 2,
                                tooltip: {
                                  placement: 'rightTop',
                                  color: '#4f606b',
                                  title: (
                                    <Text>
                                      <Text style={{ color: '#d9d9d9' }}>Tài xế: </Text>
                                      <Text style={{ color: 'white' }}>{hrTicket?.data?.driver?.fullName}</Text>
                                    </Text>
                                  )
                                }
                              }}
                            >
                              {hrTicket.data.driverId && (
                                <Text>
                                  <Text type='secondary'>Tài xế: </Text>
                                  <Text>{hrTicket?.data?.driver?.fullName}</Text>
                                </Text>
                              )}
                            </Paragraph>
                            <Paragraph
                              style={{ marginBlockEnd: 0 }}
                              ellipsis={{
                                rows: 2,
                                tooltip: {
                                  placement: 'rightTop',
                                  color: '#4f606b',
                                  title: (
                                    <Text>
                                      <Text style={{ color: '#d9d9d9' }}>Phương tiện: </Text>
                                      <Text style={{ color: 'white' }}>
                                        {hrTicket.data.vehicle?.name}. Biển số {hrTicket.data.vehicle?.licensePlate}
                                      </Text>
                                    </Text>
                                  )
                                }
                              }}
                            >
                              {hrTicket.data.vehicleId && (
                                <Text>
                                  <Text type='secondary'>Phương tiện: </Text>
                                  <Text>
                                    {hrTicket.data.vehicle?.name}. Biển số {hrTicket.data.vehicle?.licensePlate}
                                  </Text>
                                </Text>
                              )}
                            </Paragraph>
                          </Flex>
                        ))}
                      <Paragraph
                        style={{ marginBlockEnd: 0 }}
                        ellipsis={{
                          rows: 2,
                          tooltip: {
                            placement: 'rightTop',
                            color: '#4f606b',
                            title: (
                              <Flex vertical>
                                <Text>
                                  <Text style={{ color: '#d9d9d9' }}>
                                    {hrTicket.data?.confirmedType === ConfirmTypeEn.Reject
                                      ? `Lý do không xác nhận: `
                                      : `Nội dung xác nhận: `}
                                  </Text>
                                  <Text style={{ color: 'white' }}>{hrTicket.data.confirmedNote}</Text>
                                </Text>
                              </Flex>
                            )
                          }
                        }}
                      >
                        {hrTicket.data.confirmedNote && (
                          <Text>
                            <Text type='secondary'>
                              {hrTicket.data?.confirmedType === ConfirmTypeEn.Reject
                                ? `Lý do không xác nhận: `
                                : `Nội dung xác nhận: `}
                            </Text>{' '}
                            <Text>{hrTicket.data.confirmedNote}</Text>
                          </Text>
                        )}
                      </Paragraph>
                    </Flex>
                  )}
                </Flex>
              }
            />
          </List.Item>
        );
      }
    }

    // if (item.type === 'driverConfirm' && hrTicket?.data && isMatchConfirmTransportationPath) {
    //
    //   if (
    //     'confirmedType' in hrTicket?.data &&
    //     hrTicket.data.transportationTypeId === TransportationTypeEn.shuttle &&
    //     hrTicket.data.confirmedType === ConfirmTypeEn.Confirmed
    //   ) {
    //     return (
    //       <List.Item className='timeline-approval-item' key={item.type}>
    //         <List.Item.Meta
    //           avatar={
    //             <Flex className='item-left' vertical align='center' justify='start'>
    //               {hrTicket?.data.startTime && hrTicket?.data.endTime && (
    //                 <Flex vertical align='center'>
    //                   <Text strong className='time'>
    //                     {formatTime(hrTicket?.data.endTime)}
    //                   </Text>
    //                   <Text strong className='date'>
    //                     {formatDate(hrTicket?.data.endTime)}
    //                   </Text>
    //                 </Flex>
    //               )}
    //               <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
    //                 <RenderIcon
    //                   status={hrTicket?.data.startTime && hrTicket?.data.endTime ? 'approved' : 'waiting'}
    //                   className='icon-status'
    //                 />
    //               </Flex>
    //             </Flex>
    //           }
    //           title={<Text strong>{item.title}</Text>}
    //           description={
    //             <Flex vertical gap={8}>
    //               <Text>
    //                 {hrTicket.data.transportationTicketVerify
    //                   ? hrTicket.data.transportationTicketVerify.driver?.fullName
    //                   : hrTicket.data?.driver?.fullName}
    //               </Text>
    //               <Flex vertical gap={4}>
    //                 {hrTicket?.data?.startTime && (
    //                   <Text>
    //                     <Text type='secondary'>Khởi hành: </Text>
    //                     <Text>{dayjs(hrTicket?.data?.startTime).format('DD-MM-YYYY HH:mm')}</Text>
    //                   </Text>
    //                 )}
    //                 {hrTicket?.data?.endTime && (
    //                   <Text>
    //                     <Text type='secondary'>Giờ đến: </Text>
    //                     <Text>{dayjs(hrTicket?.data?.endTime).format('DD-MM-YYYY HH:mm')}</Text>
    //                   </Text>
    //                 )}
    //               </Flex>
    //             </Flex>
    //           }
    //         />
    //       </List.Item>
    //     );
    //   }
    // }

    if (item.type === 'confirmedSupervisor' && hrTicket?.data && isMatchOvertimePath) {
      if ('isSupervisorConfirmed' in hrTicket?.data) {
        return (
          <List.Item className='timeline-approval-item' key={item.type}>
            <List.Item.Meta
              avatar={
                <Flex className='item-left' vertical align='center' justify='start'>
                  <Flex vertical align='center'>
                    <Text strong className='time'>
                      {hrTicket &&
                        'isSupervisorConfirmed' in hrTicket?.data &&
                        hrTicket?.data.isSupervisorConfirmed !== ConfirmTypeEn.WaitingConfirm &&
                        formatTime(hrTicket?.data.supervisorConfirmDate)}
                    </Text>{' '}
                    <Text strong className='date'>
                      {hrTicket &&
                        'isSupervisorConfirmed' in hrTicket?.data &&
                        hrTicket?.data.isSupervisorConfirmed !== ConfirmTypeEn.WaitingConfirm &&
                        formatDate(hrTicket?.data.supervisorConfirmDate)}
                    </Text>
                  </Flex>
                  <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
                    <RenderIcon
                      status={
                        hrTicket && hrTicket?.data?.isSupervisorConfirmed === ConfirmTypeEn.Confirmed
                          ? 'approved'
                          : hrTicket?.data?.isSupervisorConfirmed === ConfirmTypeEn.Reject
                          ? 'rejected'
                          : 'waiting'
                      }
                      className='icon-status'
                    />
                  </Flex>
                </Flex>
              }
              title={<Text strong>{item.title}</Text>}
              description={
                <Flex vertical gap={5}>
                  <Text>
                    {'supervisor' in hrTicket?.data && hrTicket?.data.supervisor?.name
                      ? hrTicket?.data.supervisor?.name
                      : 'NGƯỜI GIÁM SÁT'}
                  </Text>
                </Flex>
              }
            />
          </List.Item>
        );
      }
    }

    if (item.type === 'done' && hrTicket?.data) {
      // TH cho phiếu vận chuyển
      if (isMatchConfirmTransportationPath && 'confirmedType' in hrTicket?.data) {
        return (
          <List.Item className='timeline-approval-item' key={item.type}>
            <List.Item.Meta
              avatar={
                <Flex className='item-left' vertical align='center' justify='start'>
                  {hrTicket?.data.confirmedType === ConfirmTypeEn.Confirmed && (
                    <Flex vertical align='center'>
                      <Text strong className='time'>
                        {formatTime(hrTicket?.data.confirmedDate)}
                      </Text>
                      <Text strong className='date'>
                        {formatDate(hrTicket?.data.confirmedDate)}
                      </Text>
                    </Flex>
                  )}
                  <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
                    <RenderIcon
                      status={hrTicket?.data.confirmedType === ConfirmTypeEn.Confirmed ? 'approved' : 'waiting'}
                      className='icon-status'
                    />
                  </Flex>
                </Flex>
              }
              title={<Text strong>Hoàn thành</Text>}
            />
          </List.Item>
        );
      }
      // TH cho các phiếu có HR xác nhận
      else if (hrTicket?.data.ticketStatus.id === TicketStatus.ApprovedRequest && 'hrConfirmedType' in hrTicket?.data) {
        return (
          <List.Item className='timeline-approval-item' key={item.type}>
            <List.Item.Meta
              avatar={
                <Flex className='item-left' vertical align='center' justify='start'>
                  {(hrTicket?.data.hrConfirmedType === ConfirmTypeEn.Confirmed ||
                    hrTicket?.data.hrConfirmedType === ConfirmTypeEn.Synced) && (
                    <Flex vertical align='center'>
                      <Text strong className='time'>
                        {formatTime(hrTicket?.data.hrConfirmedDate)}
                      </Text>
                      <Text strong className='date'>
                        {formatDate(hrTicket?.data.hrConfirmedDate)}
                      </Text>
                    </Flex>
                  )}
                  <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
                    <RenderIcon
                      status={
                        hrTicket?.data.hrConfirmedType === ConfirmTypeEn.Confirmed ||
                        hrTicket?.data.hrConfirmedType === ConfirmTypeEn.Synced
                          ? 'approved'
                          : 'waiting'
                      }
                      className='icon-status'
                    />
                  </Flex>
                </Flex>
              }
              title={<Text strong>{'Hoàn thành'}</Text>}
            />
          </List.Item>
        );
      }
      // TH cho các phiếu không có HR xác nhận
      else {
        return (
          <List.Item className='timeline-approval-item' key={item.type}>
            <List.Item.Meta
              avatar={
                <Flex className='item-left' vertical align='center' justify='start'>
                  {hrTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest && (
                    <Flex vertical align='center'>
                      <Text strong className='time'>
                        {formatTime(hrTicket?.data?.approvals?.reverse()?.[0]?.createdDate)}
                      </Text>
                      <Text strong className='date'>
                        {formatDate(hrTicket?.data?.approvals?.reverse()?.[0]?.createdDate)}
                      </Text>
                    </Flex>
                  )}
                  <Flex className='icon-wrapper' style={{ paddingTop: 3 }}>
                    <RenderIcon
                      status={hrTicket?.data.ticketStatus.id === TicketStatus.ApprovedRequest ? 'approved' : 'waiting'}
                      className='icon-status'
                    />
                  </Flex>
                </Flex>
              }
              title={<Text strong>{'Hoàn thành'}</Text>}
            />
          </List.Item>
        );
      }
    }
  };

  return (
    <Flex className='timeline-approval'>
      <List
        className='timeline-approval-list'
        dataSource={processTree}
        renderItem={(item: IProcessTree, index: number) => RenderItem(item, basePath as string)}
        loading={loading || approvalProcessDetailQuery.isFetching || rolesByPermissionQuery.isFetching}
      />
    </Flex>
  );
};

export default TimelineApproval;
